import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Building2, ChevronDown, LogOut } from 'lucide-react';
import { toast } from 'sonner';

export const OrgSelector = () => {
  const { 
    user, 
    api, 
    isSysAdmin, 
    selectedOrg, 
    needsOrgSelection,
    selectOrganization,
    deselectOrganization 
  } = useAuth();
  
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (isSysAdmin) {
      fetchOrganizations();
    }
  }, [isSysAdmin]);

  useEffect(() => {
    // Auto-open dialog if sys_admin needs to select an org
    if (needsOrgSelection) {
      setDialogOpen(true);
    }
  }, [needsOrgSelection]);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations?limit=100');
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handleSelectOrg = async (orgId) => {
    setLoading(true);
    try {
      const result = await selectOrganization(orgId);
      toast.success(result.message);
      setDialogOpen(false);
      // Reload page to refresh all data with new org context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select organization');
    } finally {
      setLoading(false);
    }
  };

  const handleDeselectOrg = async () => {
    setLoading(true);
    try {
      const result = await deselectOrganization();
      toast.success(result.message);
      setDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deselect organization');
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything if not sys_admin
  if (!isSysAdmin) return null;

  return (
    <>
      {/* Org indicator in header */}
      <div className="flex items-center gap-2">
        {selectedOrg ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="h-8 gap-2"
          >
            <Building2 className="w-4 h-4" />
            <span className="max-w-[150px] truncate">{selectedOrg.org_name}</span>
            <ChevronDown className="w-3 h-3" />
          </Button>
        ) : (
          <Badge variant="destructive" className="cursor-pointer" onClick={() => setDialogOpen(true)}>
            Select Organization
          </Badge>
        )}
      </div>

      {/* Org selection dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        // Don't allow closing if org selection is required
        if (!needsOrgSelection || open) {
          setDialogOpen(open);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Select Organization
            </DialogTitle>
            <DialogDescription>
              As a System Administrator, you can manage any organization. 
              Select an organization to continue.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Select
              value={selectedOrg?.org_id || ''}
              onValueChange={handleSelectOrg}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose an organization..." />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.org_id} value={org.org_id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span>{org.org_name}</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {org.org_code}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedOrg && (
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeselectOrg}
                  disabled={loading}
                  className="w-full text-muted-foreground"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Switch to different organization
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrgSelector;
